import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import {
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";

interface EmailChangeFlowProps {
  currentEmail: string;
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "form" | "confirm" | "duplicated";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailChangeFlow = ({
  currentEmail,
  onClose,
  onSubmitted,
}: EmailChangeFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const t = (key: string) => translate(`newDashboard.modifications.email.${key}`);

  const sameAsCurrent =
    newEmail.trim().toLowerCase() === currentEmail.trim().toLowerCase();
  const formValid = EMAIL_REGEX.test(newEmail.trim()) && !sameAsCurrent;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.patchContactEmail(newEmail.trim());
      onSubmitted();
      setNotification({
        message: t("successDesc"),
        type: "success",
        time: 8,
      });
      onClose();
    } catch (error: any) {
      if (error?.status === 409) {
        setStep("duplicated");
      } else {
        setNotification({
          message: translate("newDashboard.modifications.errors.generic"),
          type: "error",
          time: 5,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("current")} value={currentEmail} />
          </div>
          <Field
            label={t("newEmail")}
            error={
              showErrors && !formValid
                ? sameAsCurrent
                  ? t("sameEmail")
                  : t("invalidEmail")
                : undefined
            }
          >
            <input
              style={inputStyle}
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </Field>
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid) return setShowErrors(true);
              setStep("confirm");
            }}
          />
        </div>
      )}

      {step === "confirm" && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>{t("confirmDesc")}</p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("current")} value={currentEmail} />
            <SummaryRow label={t("newEmail")} value={newEmail.trim()} />
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirm")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("form")}
          />
        </div>
      )}


      {step === "duplicated" && (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <GlobalIcon iconName="AlertCircleIcon" size="S" color="#d97706" />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", margin: "0 0 12px" }}>
            {t("duplicatedTitle")}
          </h2>
          <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.6, margin: "0 0 28px" }}>
            {t("duplicatedDesc")}{" "}
            <a href="mailto:asesores@life5.com" style={{ color: "#1e293b", fontWeight: 600 }}>
              asesores@life5.com
            </a>
          </p>
          <ButtonDS
            label={translate("newDashboard.modifications.common.close")}
            buttonType="secondary"
            size="40"
            className="w-full"
            onClick={onClose}
          />
        </div>
      )}
    </ModificationModal>
  );
};

export default EmailChangeFlow;
